import { FilterTypes } from "@scayle/storefront-api";
import { getSortByValue } from "./sortingHelper.mjs";
export const transformToWhereCondition = (conditions) => {
  const where = { attributes: [] };
  Object.keys(conditions).forEach((key) => {
    const value = conditions[key];
    if (["sort", "term"].includes(key)) {
      return;
    }
    if (key === "minPrice" || key === "maxPrice") {
      where[key] = value;
      return;
    }
    const condition = ["true", "false"].includes(value) ? {
      key,
      type: "boolean",
      value: value === "true"
    } : {
      key,
      type: "attributes",
      values: Array.isArray(conditions[key]) ? conditions[key] : [conditions[key]]
    };
    where.attributes.push(condition);
  });
  return where;
};
const _getFilterValueByType = (value, type) => {
  switch (type) {
    case FilterTypes.BOOLEAN:
      return value.name;
    case FilterTypes.RANGE:
      return { min: value.min, max: value.max };
    default:
      return value.id;
  }
};
export const groupFilterableValuesByKey = (filters, filterableKeys, { sortable } = { sortable: true }) => {
  const enabledFilters = filterableKeys ? filters.filter((filter) => filterableKeys.includes(filter.slug)) : filters;
  const transformedAvailableFilters = enabledFilters.reduce((availableFilters, filter) => {
    filter.values.forEach((value) => {
      const isBoolean = filter.type === FilterTypes.BOOLEAN;
      const hasNameInValue = "name" in value;
      if (isBoolean && hasNameInValue && value.name === false) {
        return;
      }
      const filterValue = _getFilterValueByType(value, filter.type);
      const nameValue = hasNameInValue ? String(value.name) : "";
      availableFilters.push({
        key: filter.slug,
        displayName: isBoolean ? filter.name : nameValue,
        count: value.productCount,
        value: filterValue,
        whereCondition: {
          [filter.slug]: filterValue
        }
      });
    });
    return availableFilters;
  }, []);
  return sortable ? transformedAvailableFilters.sort(
    (a, b) => a.displayName.localeCompare(b.displayName)
  ) : transformedAvailableFilters;
};
const generateFilterKeyAndValue = (filter) => {
  return filter.reduce(
    (objectToReturn, f) => Object.assign(
      objectToReturn,
      objectToReturn[f.key] ? objectToReturn[f.key].push(f.value) : { [f.key]: [f.value] }
    ),
    {}
  );
};
export const transformStateToFilters = (state) => {
  return Object.values(state).reduce(
    (objectToReturn, filter) => Object.assign(objectToReturn, generateFilterKeyAndValue(filter)),
    {}
  );
};
export const mergeFilters = (filters) => {
  const mergedFilter = {};
  filters.forEach((item) => {
    if (!mergedFilter[item.key]) {
      mergedFilter[item.key] = item.value;
    } else {
      mergedFilter[item.key] = [mergedFilter[item.key], item.value].flat();
    }
  });
  return mergedFilter;
};
export const isFilterActive = (filters, filterToCheck) => {
  const key = filterToCheck.key;
  return filters[key] && (filters[key].toString() === filterToCheck.value.toString() || Array.isArray(filters[key]) && filters[key].includes(filterToCheck.value));
};
export const getActiveFilters = (filters, activeFilters) => {
  return [].concat(...Object.values(filters)).filter((item) => isFilterActive(activeFilters, item)) || [];
};
export const groupFiltersByKey = (filters) => {
  const groupedFilters = filters.reduce((acc, item) => {
    const groupId = item.key;
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(item);
    return acc;
  }, {});
  return Object.entries(groupedFilters);
};
export const transformMinAndMaxPriceToFilter = (prices) => {
  const [min = 0, max = 1e5] = prices;
  return {
    minPrice: min,
    maxPrice: max
  };
};
export const transformSortToFilter = (sort) => {
  return getSortByValue(sort);
};
export const getGroupedFilterableValues = (keys, filterableValues, { sortable } = { sortable: true }) => {
  return keys.reduce(
    (filtersGroup, key) => {
      filtersGroup[key] = groupFilterableValuesByKey(filterableValues, [key], {
        sortable
      });
      return filtersGroup;
    },
    {}
  );
};
export const getFilterablePriceValue = (filterableValues, key = "min") => {
  const prices = filterableValues.prices[0];
  if (!prices) {
    return key === "min" ? 0 : 1e4;
  }
  return prices.value[key];
};
export const serializeFilters = (filter) => {
  if (Object.keys(filter).length === 0) {
    return void 0;
  }
  return Object.assign(
    {},
    ...Object.entries(filter).map(([filterName, filterValue]) => {
      if (Array.isArray(filterValue)) {
        return { [filterName]: filterValue.join(",") };
      }
      if (filterValue === null || filterValue === void 0) {
        return {};
      }
      return { [filterName]: String(filterValue) };
    })
  );
};
export const deserializeFilters = (serializedFilter) => {
  const toNumberIfNumeric = (value) => {
    const number = +value;
    if (!isNaN(number)) {
      return number;
    }
    return value;
  };
  return Object.assign(
    {},
    ...Object.entries(serializedFilter).map(([filterName, filterValue]) => ({
      [filterName]: typeof filterValue === "string" && filterValue.includes(",") ? filterValue.split(",").map(toNumberIfNumeric) : toNumberIfNumeric(filterValue)
    }))
  );
};
